import { Button } from "@/components/ui/shadcn/Button/Button";
import { useEffect, useRef } from "react";

export interface AsyncSelectProps {
  search?: string;
  onSearchChange?: (value: string) => void;
  onOpenChange?: (open: boolean) => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  onRetry?: () => void;
  /** IDs matched by remote search, including matches outside the displayed label. */
  matchingValues?: string[];
  isItemsError?: boolean;
  isItemsLoading?: boolean;
}

/** A visible, keyboard-accessible fallback also loads automatically near the list bottom. */
export function AsyncSelectOptions({
  hasNextPage,
  isFetchingNextPage,
  onLoadMore,
  isItemsError,
  isItemsLoading,
  onRetry,
}: AsyncSelectProps) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (
      !ref.current ||
      !hasNextPage ||
      isItemsError ||
      isItemsLoading ||
      isFetchingNextPage
    )
      return;
    const root = ref.current.closest("[cmdk-list]");
    // Context-menu footers sit outside their scroll body; those menus use onScroll.
    if (!root) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onLoadMore?.();
      },
      { root, rootMargin: "80px" },
    );
    observer.observe(ref.current);
    return () => {
      observer.disconnect();
    };
  }, [
    hasNextPage,
    isFetchingNextPage,
    isItemsError,
    isItemsLoading,
    onLoadMore,
  ]);
  return (
    <div ref={ref} className="flex justify-center p-1" aria-live="polite">
      {isItemsError ? (
        <Button type="button" variant="ghost" size="sm" onClick={onRetry}>
          Unable to load users. Retry
        </Button>
      ) : isItemsLoading || isFetchingNextPage ? (
        <span className="text-muted-foreground p-2 text-sm">
          Loading users…
        </span>
      ) : hasNextPage ? (
        <Button type="button" variant="ghost" size="sm" onClick={onLoadMore}>
          Load more users
        </Button>
      ) : null}
    </div>
  );
}
